<template>
  <div class="sale-box">
    <form
      class="sale-box__form"
      ref="saleBox"
      id="article-form"
      :action="addToBasketUrl"
      method="POST"
      @submit.prevent="addToCart"
    >
      <div
        id="PRODUCT_DETAILS_NAME"
        class="sale-box__product-info"
      >
        <h1 class="sale-box__product-name">
          {{ productDetails.product.name }}
        </h1>
        <span class="sale-box__seo">
          {{ productDetails.product.articleNumber }}
        </span>

        <h2
          class="sale-box__product-secondary-headline sale-box__product-additional-info"
          v-if="productDetails.additionalArticleInformation"
        >
          {{ productDetails.additionalArticleInformation }}
        </h2>
        <span
          class="sale-box__product-secondary-headline"
          v-html="$t('global.article.number_with_var', [productDetails.product.number])"
        />
      </div>
      <div
        class="sale-box__product-variations"
      >
        <template
          v-for="(variation, index) in variations"
          :key="index"
        >
          <div
            class="sale-box__product-variations__wrapper"
            v-if="Object.keys(variation.options).length"
          >
            <product-variations
              class="sale-box__variations"
              :name="variation.name"
              :update="updateVariationUrl"
              :display_string="variation.display_string"
              :variations="variation.options"
              :initial-selected-value="variation.selectedOption"
              :edit-product="true"
              :index="index"
              @changeVariation="productUpdateHandler"
              ref="productVariation"
            />
          </div>
        </template>
      </div>
      <div
        v-if="isIndividualRequestAvailable"
      >
        <b class="sale-box__individual-request-label">
          {{ $t("global.individual.text") }}
        </b>
        <div class="sale-box__individual-request-text">
          {{ getIndividualRequestInfoText }}
        </div>
      </div>
      <div
        class="sale-box__graduated-prices__wrapper"
        v-else-if="productDetails.product.graduatedPrices.length > 1"
      >
        <product-graduated-prices
          class="sale-box__graduated-prices"
          :graduated_prices="productDetails.product.graduatedPrices"
          :display_string="productDetails.product.packagingType"
          :initial-value="productDetails.product.selectedQuantity"
          :max="max"
          @change="graduatedPricesChangeHandler"
          ref="graduatedPrices"
          :edit-product="true"
        />
      </div>
      <div class="sale-box__amount-price-wrapper">
        <div class="sale-box__amount-price-headline-wrapper">
          <span class="sale-box__amount-price-wrapper--headline sale-box__main-amount">
            {{ $t('ads.main.amount') }}
          </span>
          <span class="sale-box__amount-price-wrapper--headline">
            {{ $t('ads.main.total_sum') }}
          </span>
        </div>
        <div class="sale-box__amount-price-body-wrapper">
          <div
            class="sale-box__qty-wrapper"
          >
            <counter
              class="sale-box__counter"
              :initial-value="productDetails.product.selectedQuantity"
              :min="productDetails.product.minimumOrderQuantity"
              :max="max"
              :step="productDetails.product.salesVolumeFactor"
              @change="counterChangeHandler"
              name="quantity"
              :current-predefined-quantity-index="10"
              :non-valid-input-message="$t('counter.nonvalid.value')"
              :rounding-message="$t('counter.round.value')"
              :graduated-prices="productDetails.product.graduatedPrices.length > 1"
              ref="quantity"
            />
          </div>

          <div
            v-if="!isIndividualRequestAvailable"
            class="sale-box__price-wrapper"
          >
            <div
              id="PRODUCT_DETAILS_PRICE"
              class="sale-box__price-left"
            >
              <span
                class="sale-box__savings"
                v-if="priceAvailability.hasSaving"
                v-html="$t('ads.ads_top_info.you_save.amount', [priceAvailability.totalSaving])"
              />
              <div class="sale-box__price-base-container">
                <div class="sale-box__price-base-wrapper">
                  <span
                    class="sale-box__price sale-box__net-price--base"
                  >
                    <div>{{ priceAvailability.totalPrice }}</div>
                  </span>
                  <div class="sale-box__price-base-wrapper-addition">
                    {{ $t('ads.main.total_sum.excluding.tax') }}
                  </div>
                </div>
                <div class="sale-box__price-base-wrapper">
                  <span
                    class="sale-box__price sale-box__price--base"
                  >
                    <div>{{ priceAvailability.grossTotalPrice }}</div>
                  </span>
                  <div class="sale-box__price-base-wrapper-addition">
                    {{ $t('ads.main.total_sum.including.tax') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="sale-box__buttons">
        <button
          v-if="isIndividualRequestAvailable"
          class="sale-box__button-to-cart sale-box__button-to-cart--desktop | button button--alt button--add-to-cart"
          :class="[isAddedToCart ? 'sale-box__button-to-cart__clicked' : 'sale-box__button-to-cart__not-clicked']"
          name="ADD_TO_CART_BTN_DESKTOP"
          v-html="$t('global.individual.text')"
          @click.prevent="contactFormularForIndividualRequest"
        />
        <button
          v-else-if="this.productDetails.printDetails.hasPrintData"
          class="sale-box__button-to-cart sale-box__button-to-cart--desktop | button button--alt button--add-to-cart"
          :class="[isAddedToCart ? 'sale-box__button-to-cart__clicked' : 'sale-box__button-to-cart__not-clicked']"
          :disabled="checkLongAvailabilityTextAvailability"
          name="ADD_TO_CART_BTN_DESKTOP"
          type="submit"
          v-html="$t('global.print.basket.text')"
          @click.prevent="togglePrintModal"
        />
        <button
          v-else
          class="sale-box__button-to-cart sale-box__button-to-cart--desktop | button button--alt button--add-to-cart"
          :class="[isAddedToCart ? 'sale-box__button-to-cart__clicked' : 'sale-box__button-to-cart__not-clicked']"
          :disabled="checkLongAvailabilityTextAvailability"
          id="ADD_TO_CART_BTN"
          type="submit"
          name="ADD_TO_CART_BTN_DESKTOP"
          v-html="isAddedToCart ? $t('global.addedToBasket.desktop'): $t('global.addToBasket')"
          @click="gtmAddToCart"
        />
        <button
          v-if="isIndividualRequestAvailable"
          class="sale-box__button-to-cart sale-box__button-to-cart--mobile | button button--alt button--add-to-cart"
          :class="[isAddedToCart ? 'sale-box__button-to-cart__clicked' : 'sale-box__button-to-cart__not-clicked']"
          name="ADD_TO_CART_BTN_MOBILE"
          v-html="$t('global.individual.text')"
          @click.prevent="contactFormularForIndividualRequest"
        />
        <button
          v-else-if="this.productDetails.printDetails.hasPrintData"
          class="sale-box__button-to-cart sale-box__button-to-cart--mobile | button button--alt button--add-to-cart"
          :class="[isAddedToCart ? 'sale-box__button-to-cart__clicked' : 'sale-box__button-to-cart__not-clicked']"
          :disabled="checkLongAvailabilityTextAvailability"
          name="ADD_TO_CART_BTN_MOBILE"
          type="submit"
          v-html="$t('global.print.basket.text')"
          @click.prevent="togglePrintModal"
        />
        <button
          v-else
          class="sale-box__button-to-cart sale-box__button-to-cart--mobile | button button--alt button--add-to-cart"
          :class="[isAddedToCart ? 'sale-box__button-to-cart__clicked' : 'sale-box__button-to-cart__not-clicked']"
          :disabled="checkLongAvailabilityTextAvailability"
          type="submit"
          name="ADD_TO_CART_BTN_MOBILE"
          v-html="isAddedToCart ? $t('global.addedToBasket.desktop'): $t('global.addToBasket')"
          @click="gtmAddToCart"
        />
      </div>
    </form>

    <div
      v-if="!isIndividualRequestAvailable"
      class="sale-box__infos"
    >
      <div
        class="sale-box__availability-info"
        :class="priceAvailability.availability.color"
      >
        <span
          class="sale-box__info sale-box__info--availability"
          v-html="priceAvailability.availability.text"
        />
      </div>

      <div
        class="sale-box__price-info"
      >
        <a
          v-if="checkLongAvailabilityTextAvailability"
          class="sale-box____stock_notification"
          :href="contactUrl"
          v-html="$t('ads.ads_top_info.stock_notification')"
        />
        <span
          v-else
          class="sale-box__shipping-size"
          v-html="expectedQuantityText"
        />
      </div>
    </div>
    <print-modal
      v-if="displayPrintModal"
      :get-form-data="getFormData"
      :click-on-cart-handler="clickOnCartHandler"
      @success="resetAndCloseModal"
      @cancel="closeModal"
    />
  </div>
</template>

<script>
  import Counter from '../counter/counter.vue';
  import ProductVariations from '../productVariations/productVariations.vue';
  import ProductGraduatedPrices from '../productGraduatedPrices/productGraduatedPrices.vue';
  import PrintModal from '../printModal/printModal.vue'
  import AddToBasketMixin from "../../utilities/vueMixins/addToBasketMixin/addToBasketMixin";
  import dataLayerMixin from "../../utilities/vueMixins/dataLayerMixin/dataLayerMixin";
  import pigeon from '../../utilities/js/pigeon/pigeon.js';
  import productPageStateComputedMixin
    from "../../utilities/vueMixins/productPageStateComputedMixin/productPageStateComputedMixin";

  export default {
    components: {
      Counter,
      ProductVariations,
      ProductGraduatedPrices,
      PrintModal
    },
    mixins: [
      AddToBasketMixin,
      dataLayerMixin,
      productPageStateComputedMixin,
    ],
    props: {
      max: {
        type: Number,
        default: Number.MAX_VALUE
      }
    },
    data() {
      return {
        isAddedToCart: false,
        displayPrintModal: false,
        clickSubmit: true
      };
    },
    computed: {
      updateVariationUrl() {
        let urlPrefix = window.location.href.split('itemId=')[0];
        if (window.location.href === urlPrefix) {
          urlPrefix = urlPrefix + '#';
        }
        const url = urlPrefix + 'itemId=' + this.productDetails.product.number;
        history.replaceState(
            {},
            null,
            url
        );
        return url;
      },
      checkLongAvailabilityTextAvailability() {
        return this.compareLongAvailabilityText(this.priceAvailability.availability);
      },
      contactUrl() {
        return this.productDetails.product.contactFormUrl.replace('%7Bquantity%7D', this.productDetails.product.selectedQuantity);
      },
      expectedQuantityText() {
        if (!this.quantityPerPalette) {
          return "";
        } else {
          const selectedQuantity = this.productDetails.product.selectedQuantity;
          if (!this.priceAvailability.availability.longTimeAvailability && this.quantityPerPalette > selectedQuantity) {
            return this.$t('global.expectedQuantity.parcel_shipping');
          }
          const palleteSize = Math.round((selectedQuantity / this.quantityPerPalette) * 100) / 100;
          if (palleteSize === 1) {
            return this.$t('global.expectedQuantityPallet.label.one')
          }
          const palletSizeAsString = palleteSize.toLocaleString(
            'de-DE',
            {
              maximumFractionDigits: 2,
              trailingZeroDisplay: 'stripIfInteger'
            }
          );
          return this.$t('global.expectedQuantityPallet.label', [palletSizeAsString]);
        }
      },
      getIndividualRequestInfoText() {
        return this.$store.state.productPage.productData.individualRequestInfo.individualRequestText;
      },
      isIndividualRequestAvailable() {
        return this.$store.state.productPage.productData.individualRequestInfo.individualRequestAvailable;
      }
    },
    mounted() {
      this.gtmVariationChange();
      pigeon.subscribe('salebox:setcounter', quantity => {
        this.$refs.quantity.setValue(parseInt(quantity));
      });
      const self = this
      window.addEventListener('pointerdown', () => {
        self.clickSubmit = true;
      })

      window.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.code === 'Space' || e.key === 'Tab') {
          self.clickSubmit = false
        }
      })
    },
    methods: {
      gtmAddToCart() {
        this.trackEvent({
          event: "eec.add_to_cart",
          ecommerce: {
            currencyCode: this.currencyCode,
            add: {
              products: this.productInformation('add_to_cart')
            }
          }
        });
      },
      gtmVariationChange() {
        this.trackEvent({
          event: "eec.view_item",
          ecommerce: {
            currencyCode: this.currencyCode,
            detail: {
              products: this.productInformation('view_item')
            }
          }
        });
      },
      selectedVariations() {
        const variations = this.productDetails.product.selectedVariations
          .filter(element => {
            return element !== undefined;
          });
        return this.sortVariant(variations);
      },
      productInformation(type) {
        let product = this.productDetails.product;
        let productInformation = [];
        productInformation.push({
          id: product.itemId,
          itemGroupId: product.id,
          name: product.name,
          categoryId: product.categoryId,
          categoryName: product.categoryName,
          variant: this.selectedVariations(),
          primaryImageUrl: this.productDetails.images[0]?.big
        })

        if (type === 'add_to_cart') {
          // Bezogen auf die dem Warenkorb hinzugefügte Menge
          const quantity = this.$refs.quantity.getValue();
          const activeGraduatedPrice = this.$refs.graduatedPrices.getActiveGraduatedPrice();
          const priceToTrack = activeGraduatedPrice !== undefined ? activeGraduatedPrice.price : undefined;
          productInformation[0].pricePerItemNet = this.priceToFloat(priceToTrack);
          productInformation[0].quantity = quantity;
        } else {
          if (product.graduatedPrices[0]) {
            // Bezogen auf die minimal auswählbare Menge
            productInformation[0].pricePerItemNet = this.priceToFloat(product.graduatedPrices[0].price);
            productInformation[0].pricePerItemStartingAtNet = this.priceToFloat(product.graduatedPrices[product.graduatedPrices.length - 1].price);
          }
        }
        return productInformation;
      },
      counterChangeHandler(quantity) {
        this.$store.dispatch("productPage/quantityChanged", {
          'X-CSRF-TOKEN': `${window.__globalConfig.csrfToken.value}`,
          itemId: this.productDetails.product.itemId,
          quantity
        })
      },
      graduatedPricesChangeHandler() {
        const amount = this.$refs.graduatedPrices.getAmount();
        this.counterChangeHandler(amount);
      },
      addToCart() {
        this.$store.dispatch('productPage/addToCart', {
          formData: this.getFormData(),
          url: this.addToBasketUrl,
          itemId: this.productDetails.product.itemId,
          temporary: this.clickSubmit
        });
        this.clickOnCartHandler();
      },
      clickOnCartHandler() {
        this.isAddedToCart = true;
        const self = this;
        setTimeout(function () {
          self.isAddedToCart = false;
        }, 3000)
      },
      productUpdateHandler() {
        this.$store.dispatch('productPage/productChanged', {
          formData: this.getFormData(),
          url: this.productUpdatePathUrl
        })
        setTimeout(() => {
          this.gtmVariationChange();
        }, 100)
      },
      getFormData() {
        let data = {};
        data[window.__globalConfig.csrfToken.name] = window.__globalConfig.csrfToken.value;
        data["productId"] = this.productDetails.product.id;
        data["itemId"] = this.productDetails.product.itemId;
        // used for tracking
        data["categoryId"] = this.productDetails.product.categoryId;

        data[this.$refs.quantity.name] = this.$refs.quantity.getValue();

        if (this.$refs.productVariation) {
          this.$refs.productVariation.map((productVariation, index) => {
            data[this.productDetails.product.variations[index].name] = productVariation.getValue();
          });
        }
        return data;
      },
      referToFriendHandler() {
        this.$store.dispatch('showModal', { id: 'refer-to-a-friend-modal', action: 'open' })
      },
      contactFormularForIndividualRequest() {
        document.location.href = this.productDetails.product.contactFormUrlForIndividualRequest.replace('%7Bquantity%7D', this.productDetails.product.selectedQuantity);
      },
      togglePrintModal() {
        this.displayPrintModal = !this.displayPrintModal;
      },
      resetAndCloseModal() {
        document.location.href = "/basket"
      },
      closeModal() {
        this.togglePrintModal();
        this.$store.state.isOverlayVisible = false;
      }
    }
  };

</script>
